import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Award, CheckCircle, XCircle, Shield, Calendar, User, BookOpen, ArrowLeft, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CertificateVerify = () => {
  const { code } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState(null);

  useEffect(() => {
    const verifyCertificate = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API}/certificates/verify/${code}`);
        setResult(response.data);
      } catch (error) {
        setResult({ valid: false, message: 'Doğrulama sırasında bir hata oluştu' });
      } finally {
        setLoading(false);
      }
    };
    
    verifyCertificate();
  }, [code]);

  const retryVerification = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/certificates/verify/${code}`);
      setResult(response.data);
    } catch (error) {
      setResult({ valid: false, message: 'Doğrulama sırasında bir hata oluştu' });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-amber-400 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-blue-200">Sertifika doğrulanıyor...</p>
          <p className="text-blue-300/50 text-sm mt-2">Kod: {code}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 py-8 px-4">
      {/* Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-20 w-72 h-72 bg-blue-500/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl animate-pulse"></div>
      </div>

      {/* Header */}
      <div className="relative z-10 max-w-2xl mx-auto mb-8">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-white/70 hover:text-white transition-colors mb-4"
        >
          <ArrowLeft className="w-5 h-5" />
          Ana Sayfaya Dön
        </button>
        
        {/* Logo */}
        <div className="text-center mb-6">
          <div className="flex items-center justify-center gap-3 mb-2">
            <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg">
              <svg width="32" height="32" viewBox="0 0 48 48" fill="none">
                <path d="M8 24L42 8L32 42L24 28L8 24Z" fill="white" fillOpacity="0.9" />
                <path d="M8 24L42 8L24 28L8 24Z" fill="white" />
              </svg>
            </div>
          </div>
          <h1 className="text-2xl font-bold">
            <span className="text-blue-400">air</span>
            <span className="text-white">academia</span>
          </h1>
          <p className="text-blue-200/60 text-sm mt-1">Sertifika Doğrulama Sistemi</p>
        </div>
      </div>

      {/* Result Card */}
      <div className="relative z-10 max-w-2xl mx-auto">
        {result?.valid ? (
          <Card className="border-0 bg-white/10 backdrop-blur-lg overflow-hidden">
            {/* Success Header */}
            <div className="bg-gradient-to-r from-green-500 to-emerald-500 p-6 text-center">
              <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                <CheckCircle className="w-12 h-12 text-green-500" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Sertifika Doğrulandı</h2>
              <p className="text-green-100">Bu sertifika geçerli ve AirAcademia tarafından verilmiştir.</p>
            </div>

            <CardContent className="p-6">
              {/* Certificate Details */}
              <div className="space-y-6">
                {/* Recipient */}
                <div className="flex items-center gap-4 p-4 bg-white/5 rounded-xl">
                  <div className="w-14 h-14 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl flex items-center justify-center">
                    <User className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <p className="text-blue-200/60 text-sm">Sertifika Sahibi</p>
                    <h3 className="text-xl font-bold text-white">{result.certificate?.user_name}</h3>
                  </div>
                </div>

                {/* Certificate Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-4 bg-white/5 rounded-xl">
                    <div className="flex items-center gap-2 mb-2">
                      <Award className="w-5 h-5 text-amber-400" />
                      <span className="text-blue-200/60 text-sm">Sertifika Adı</span>
                    </div>
                    <p className="text-white font-medium">{result.certificate?.title}</p>
                  </div>

                  <div className="p-4 bg-white/5 rounded-xl">
                    <div className="flex items-center gap-2 mb-2">
                      <BookOpen className="w-5 h-5 text-blue-400" />
                      <span className="text-blue-200/60 text-sm">Kategori</span>
                    </div>
                    <p className="text-white font-medium">{result.certificate?.category}</p>
                  </div>

                  <div className="p-4 bg-white/5 rounded-xl">
                    <div className="flex items-center gap-2 mb-2">
                      <Calendar className="w-5 h-5 text-green-400" />
                      <span className="text-blue-200/60 text-sm">Veriliş Tarihi</span>
                    </div>
                    <p className="text-white font-medium">{result.certificate?.issue_date}</p>
                  </div>

                  <div className="p-4 bg-white/5 rounded-xl">
                    <div className="flex items-center gap-2 mb-2">
                      <Shield className="w-5 h-5 text-amber-400" />
                      <span className="text-blue-200/60 text-sm">Doğrulama Kodu</span>
                    </div>
                    <p className="text-white font-mono font-medium">{result.certificate?.code}</p>
                  </div>
                </div>

                {/* Verification Badge */}
                <div className="flex items-center justify-center p-4 bg-green-500/10 border border-green-500/30 rounded-xl">
                  <Shield className="w-5 h-5 text-green-400 mr-2" />
                  <span className="text-green-400 font-medium">Bu belge doğrulanmış ve geçerlidir</span>
                </div>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-0 bg-white/10 backdrop-blur-lg overflow-hidden">
            {/* Error Header */}
            <div className="bg-gradient-to-r from-red-500 to-rose-500 p-6 text-center">
              <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                <XCircle className="w-12 h-12 text-red-500" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Sertifika Doğrulanamadı</h2>
              <p className="text-red-100">{result?.message || 'Bu sertifika bulunamadı veya geçersiz.'}</p>
            </div>

            <CardContent className="p-6 text-center">
              <p className="text-white/70 mb-4">
                Girilen doğrulama kodu sistemimizde bulunamadı. Lütfen kodun doğru olduğundan emin olun.
              </p>
              <p className="text-white/50 text-sm mb-6">
                Aranan Kod: <span className="font-mono text-amber-400">{code}</span>
              </p>
              <div className="flex items-center justify-center gap-3">
                <Button 
                  onClick={() => navigate('/')}
                  variant="outline"
                  className="border-white/20 text-white hover:bg-white/10"
                >
                  Ana Sayfaya Dön
                </Button>
                <Button 
                  onClick={retryVerification}
                  className="bg-amber-500 hover:bg-amber-600"
                >
                  Tekrar Dene
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Footer Info */}
        <div className="text-center mt-8">
          <p className="text-blue-200/40 text-sm">
            AirAcademia sertifikaları QR kod veya doğrulama kodu ile doğrulanabilir.
          </p>
          <a 
            href="https://airacademia.com" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-blue-400 hover:text-blue-300 text-sm mt-2"
          >
            airacademia.com <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      </div>
    </div>
  );
};

export default CertificateVerify;
